import {
  partnerCompanyService,
  type PartnerCompanyAccountCreateRequest,
  type PartnerCompanyAccountUpdateRequest,
  type PartnerCompanyAssignmentCreateRequest,
  type PartnerCompanyCreateRequest,
  type PartnerCompanyContractCreateRequest,
  type PartnerCompanyContractUpdateRequest,
  type PartnerCompanySearchRequest,
  type PartnerCompanyUpdateRequest,
} from '@/entities/partner-company/api/partner-company.api';
import type { Pageable } from '@/entities/user/api/user.api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const partnerCompanyKeys = {
  all: ['partner-companies'] as const,
  lists: () => [...partnerCompanyKeys.all, 'list'] as const,
  list: (search: PartnerCompanySearchRequest, pageable: Pageable) =>
    [...partnerCompanyKeys.lists(), search, pageable] as const,
  options: () => [...partnerCompanyKeys.all, 'options'] as const,
  details: () => [...partnerCompanyKeys.all, 'detail'] as const,
  detail: (partnerCompanyId: number) =>
    [...partnerCompanyKeys.details(), partnerCompanyId] as const,
  histories: (partnerCompanyId: number) =>
    [...partnerCompanyKeys.all, 'histories', partnerCompanyId] as const,
  accounts: (partnerCompanyId: number) =>
    [...partnerCompanyKeys.all, 'accounts', partnerCompanyId] as const,
};

export interface PartnerCompanyListParams {
  search: PartnerCompanySearchRequest;
  pageable: Pageable;
}

export const usePartnerCompanyList = ({
  search,
  pageable,
}: PartnerCompanyListParams) =>
  useQuery({
    queryKey: partnerCompanyKeys.list(search, pageable),
    queryFn: () => partnerCompanyService.getPartnerCompanies(search, pageable),
  });

export const usePartnerCompanyOptions = () =>
  useQuery({
    queryKey: partnerCompanyKeys.options(),
    queryFn: () => partnerCompanyService.getPartnerCompanyOptions(),
  });

export const usePartnerCompanyDetail = (partnerCompanyId: number | null) =>
  useQuery({
    queryKey:
      partnerCompanyId != null ? partnerCompanyKeys.detail(partnerCompanyId) : [],
    queryFn: async () => {
      if (partnerCompanyId == null) {
        throw new Error('partnerCompanyId is required');
      }
      return partnerCompanyService.getPartnerCompanyDetail(partnerCompanyId);
    },
    enabled: partnerCompanyId != null,
  });

export const usePartnerCompanyHistories = (partnerCompanyId: number | null) =>
  useQuery({
    queryKey:
      partnerCompanyId != null
        ? partnerCompanyKeys.histories(partnerCompanyId)
        : [],
    queryFn: async () => {
      if (partnerCompanyId == null) {
        throw new Error('partnerCompanyId is required');
      }
      return partnerCompanyService.getHistories(partnerCompanyId);
    },
    enabled: partnerCompanyId != null,
  });

export const useCreatePartnerCompany = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: PartnerCompanyCreateRequest) =>
      partnerCompanyService.createPartnerCompany(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: partnerCompanyKeys.lists() });
      queryClient.invalidateQueries({ queryKey: partnerCompanyKeys.options() });
    },
  });
};

export const useUpdatePartnerCompany = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      partnerCompanyId,
      data,
    }: {
      partnerCompanyId: number;
      data: PartnerCompanyUpdateRequest;
    }) => partnerCompanyService.updatePartnerCompany(partnerCompanyId, data),
    onSuccess: (_data, { partnerCompanyId }) => {
      queryClient.invalidateQueries({ queryKey: partnerCompanyKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: partnerCompanyKeys.detail(partnerCompanyId),
      });
      queryClient.invalidateQueries({
        queryKey: partnerCompanyKeys.histories(partnerCompanyId),
      });
      queryClient.invalidateQueries({ queryKey: partnerCompanyKeys.options() });
    },
  });
};

export const useCreatePartnerCompanyContract = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      partnerCompanyId,
      data,
    }: {
      partnerCompanyId: number;
      data: PartnerCompanyContractCreateRequest;
    }) => partnerCompanyService.createContract(partnerCompanyId, data),
    onSuccess: (_data, { partnerCompanyId }) => {
      queryClient.invalidateQueries({
        queryKey: partnerCompanyKeys.detail(partnerCompanyId),
      });
      queryClient.invalidateQueries({
        queryKey: partnerCompanyKeys.histories(partnerCompanyId),
      });
      queryClient.invalidateQueries({ queryKey: partnerCompanyKeys.lists() });
    },
  });
};

export const useUpdatePartnerCompanyContract = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      partnerCompanyId,
      contractId,
      data,
    }: {
      partnerCompanyId: number;
      contractId: number;
      data: PartnerCompanyContractUpdateRequest;
    }) =>
      partnerCompanyService.updateContract(
        partnerCompanyId,
        contractId,
        data,
      ),
    onSuccess: (_data, { partnerCompanyId }) => {
      queryClient.invalidateQueries({
        queryKey: partnerCompanyKeys.detail(partnerCompanyId),
      });
      queryClient.invalidateQueries({
        queryKey: partnerCompanyKeys.histories(partnerCompanyId),
      });
      queryClient.invalidateQueries({ queryKey: partnerCompanyKeys.lists() });
    },
  });
};

export const useCreatePartnerCompanyDocument = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      partnerCompanyId,
      data,
    }: {
      partnerCompanyId: number;
      data: {
        documentType: string;
        originalFileName: string;
        s3FileKey: string;
      };
    }) => partnerCompanyService.createDocument(partnerCompanyId, data),
    onSuccess: (_data, { partnerCompanyId }) => {
      queryClient.invalidateQueries({
        queryKey: partnerCompanyKeys.detail(partnerCompanyId),
      });
      queryClient.invalidateQueries({
        queryKey: partnerCompanyKeys.histories(partnerCompanyId),
      });
    },
  });
};

export const useCreatePartnerCompanyAssignment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      parentCompanyId,
      data,
    }: {
      parentCompanyId: number;
      data: PartnerCompanyAssignmentCreateRequest;
    }) => partnerCompanyService.createAssignment(parentCompanyId, data),
    onSuccess: (_data, { parentCompanyId }) => {
      queryClient.invalidateQueries({
        queryKey: partnerCompanyKeys.detail(parentCompanyId),
      });
      queryClient.invalidateQueries({
        queryKey: partnerCompanyKeys.histories(parentCompanyId),
      });
    },
  });
};

export const useCancelPartnerCompanyAssignment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      partnerCompanyId,
      assignmentId,
    }: {
      partnerCompanyId: number;
      assignmentId: number;
    }) =>
      partnerCompanyService.cancelAssignment(partnerCompanyId, assignmentId),
    onSuccess: (_data, { partnerCompanyId }) => {
      queryClient.invalidateQueries({
        queryKey: partnerCompanyKeys.detail(partnerCompanyId),
      });
      queryClient.invalidateQueries({
        queryKey: partnerCompanyKeys.histories(partnerCompanyId),
      });
    },
  });
};

/** 파트너스테이션 계정 목록 — 파트너사를 고르기 전에는 조회하지 않는다. */
export const usePartnerCompanyAccounts = (partnerCompanyId: number | null) =>
  useQuery({
    queryKey:
      partnerCompanyId != null
        ? partnerCompanyKeys.accounts(partnerCompanyId)
        : [],
    queryFn: async () => {
      if (partnerCompanyId == null) {
        throw new Error('partnerCompanyId is required');
      }
      return partnerCompanyService.getAccounts(partnerCompanyId);
    },
    enabled: partnerCompanyId != null,
  });

export const useCreatePartnerCompanyAccount = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      partnerCompanyId,
      data,
    }: {
      partnerCompanyId: number;
      data: PartnerCompanyAccountCreateRequest;
    }) => partnerCompanyService.createAccount(partnerCompanyId, data),
    onSuccess: (_data, { partnerCompanyId }) => {
      queryClient.invalidateQueries({
        queryKey: partnerCompanyKeys.accounts(partnerCompanyId),
      });
    },
  });
};

export const useUpdatePartnerCompanyAccount = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      partnerCompanyId,
      accountId,
      data,
    }: {
      partnerCompanyId: number;
      accountId: number;
      data: PartnerCompanyAccountUpdateRequest;
    }) =>
      partnerCompanyService.updateAccount(partnerCompanyId, accountId, data),
    onSuccess: (_data, { partnerCompanyId }) => {
      queryClient.invalidateQueries({
        queryKey: partnerCompanyKeys.accounts(partnerCompanyId),
      });
    },
  });
};
